import { useState, useEffect, useRef } from 'react'
import { Grid, Text, Center } from '@chakra-ui/react'
import { useTrail } from 'react-spring'

import {
  BlogItemSkeleton,
  BlogItem,
} from '@/features/blogs/components/BlogItem'

import { useQueryParamsContext } from '@/lib/context/queryParams'
import { DEFAULT_SPRING_CONFIG } from '@/lib/springComponent'

import { BlogListItem } from '@/features/blogs/types/blog'

interface Props {
  id?: string
  blogs: BlogListItem[]
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const debounceTime = 1000

function BlogList({ id, blogs }: Props): React.ReactElement {
  const timeRef = useRef(0)

  const { isLoading } = useQueryParamsContext()

  const [_isLoading, _setIsLoading] = useState(isLoading)

  useEffect(() => {
    const currentTimestamp = new Date().getTime()

    if (isLoading) {
      _setIsLoading(true)

      timeRef.current = currentTimestamp

      return noop
    }

    if (currentTimestamp - timeRef.current >= debounceTime) {
      _setIsLoading(false)

      return noop
    } else {
      const timer = setTimeout(() => {
        _setIsLoading(false)
      }, debounceTime - (currentTimestamp - timeRef.current))

      return () => clearTimeout(timer)
    }
  }, [isLoading])

  const trail = useTrail(blogs.length || 0, {
    from: {
      transform: 'translate3d(0, 32px, 0)',
      opacity: 0,
    },
    to: {
      transform: _isLoading
        ? 'translate3d(0, 0px, 0)'
        : 'translate3d(0, 32px, 0)',
      opacity: _isLoading ? 1 : 0,
    },
    immediant: !_isLoading,
    config: DEFAULT_SPRING_CONFIG,
  })

  if (blogs.length <= 0) {
    return (
      <Center layerStyle="container">
        <Text
          sx={{
            fontSize: '3xl',
            fontWeight: 'medium',
            color: 'red.300',
            textDecoration: 'underline',
          }}
        >
          ไม่พบผลการค้นหา
        </Text>
      </Center>
    )
  }

  return (
    <Grid
      id={id}
      layerStyle="container"
      sx={{
        gridTemplateColumns: {
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gridColumnGap: 4,
        gridRowGap: 6,
        pb: 8,
      }}
    >
      {blogs.map((blog, index) => {
        return !_isLoading ? (
          <BlogItem key={blog.slug} data={blog} />
        ) : (
          <BlogItemSkeleton key={blog.slug} style={trail[index]} />
        )
      })}
    </Grid>
  )
}

export default BlogList
